import { Close, Crop, PhotoCamera } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Slider,
  Stack,
  Typography,
} from "@mui/material";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";

type StorageImageCropFieldProps = {
  label: string;
  helperText: string;
  value?: string;
  aspectRatio: number;
  uploading?: boolean;
  uploadProgress?: number;
  onUpload: (file: File) => Promise<string>;
  onChange: (url: string) => void;
};

const outputWidthFor = (aspectRatio: number) => aspectRatio > 1 ? 1600 : 1024;

export const StorageImageCropField = ({
  label,
  helperText,
  value,
  aspectRatio,
  uploading = false,
  uploadProgress = 0,
  onUpload,
  onChange,
}: StorageImageCropFieldProps) => {
  const fileInput = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const sourceUrlRef = useRef("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [imageReady, setImageReady] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [horizontal, setHorizontal] = useState(0);
  const [vertical, setVertical] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const drawCrop = useCallback(() => {
    const image = imageRef.current;
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!image || !canvas || !context) return;

    const outputWidth = outputWidthFor(aspectRatio);
    const outputHeight = Math.round(outputWidth / aspectRatio);
    canvas.width = outputWidth;
    canvas.height = outputHeight;

    const imageRatio = image.naturalWidth / image.naturalHeight;
    const baseWidth = imageRatio > aspectRatio ? image.naturalHeight * aspectRatio : image.naturalWidth;
    const baseHeight = imageRatio > aspectRatio ? image.naturalHeight : image.naturalWidth / aspectRatio;
    const cropWidth = baseWidth / zoom;
    const cropHeight = baseHeight / zoom;
    const maxLeft = image.naturalWidth - cropWidth;
    const maxTop = image.naturalHeight - cropHeight;
    const left = Math.max(0, Math.min(maxLeft, maxLeft / 2 + horizontal * maxLeft / 2));
    const top = Math.max(0, Math.min(maxTop, maxTop / 2 + vertical * maxTop / 2));

    context.clearRect(0, 0, outputWidth, outputHeight);
    context.drawImage(image, left, top, cropWidth, cropHeight, 0, 0, outputWidth, outputHeight);
  }, [aspectRatio, horizontal, vertical, zoom]);

  useEffect(() => {
    if (!sourceUrl) return;
    const image = new Image();
    image.onload = () => {
      imageRef.current = image;
      setImageReady(true);
      drawCrop();
    };
    image.onerror = () => setError("No pudimos abrir esa imagen. Prueba con otro archivo.");
    image.src = sourceUrl;
    return () => { imageRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceUrl]);

  useEffect(() => {
    if (imageReady) drawCrop();
  }, [drawCrop, imageReady]);

  useEffect(() => () => {
    if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current);
  }, []);

  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Selecciona un archivo de imagen.");
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      setError("La imagen debe pesar menos de 12 MB.");
      return;
    }
    if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current);
    const nextUrl = URL.createObjectURL(file);
    sourceUrlRef.current = nextUrl;
    setError("");
    setImageReady(false);
    setZoom(1);
    setHorizontal(0);
    setVertical(0);
    setSourceUrl(nextUrl);
  };

  const cancelCrop = () => {
    if (saving || uploading) return;
    setSourceUrl("");
    setImageReady(false);
    imageRef.current = null;
    if (sourceUrlRef.current) URL.revokeObjectURL(sourceUrlRef.current);
    sourceUrlRef.current = "";
  };

  const uploadCroppedImage = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setSaving(true);
    setError("");
    try {
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((result) => result ? resolve(result) : reject(new Error("No pudimos procesar el recorte.")), "image/png");
      });
      const file = new File([blob], `${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`, { type: "image/png" });
      const imageUrl = await onUpload(file);
      onChange(imageUrl);
      setSourceUrl("");
      sourceUrlRef.current && URL.revokeObjectURL(sourceUrlRef.current);
      sourceUrlRef.current = "";
      setImageReady(false);
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : "No pudimos subir la imagen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">{label}</Typography>
      {value ? (
        <Box component="img" src={value} alt={label} sx={{ width: "100%", maxWidth: 360, aspectRatio, objectFit: "cover", borderRadius: 1, border: "1px solid", borderColor: "divider" }} />
      ) : (
        <Box sx={{ width: "100%", maxWidth: 360, aspectRatio, display: "grid", placeItems: "center", border: "1px dashed", borderColor: "divider", borderRadius: 1, color: "text.secondary" }}>
          <Typography variant="body2">Aún no has subido una imagen</Typography>
        </Box>
      )}
      <Typography variant="body2" color="text.secondary">{helperText}</Typography>
      <Stack direction="row" spacing={1}>
        <Button variant="outlined" size="small" startIcon={<PhotoCamera />} onClick={() => fileInput.current?.click()} disabled={uploading || saving}>
          {value ? "Cambiar imagen" : "Subir imagen"}
        </Button>
        {value ? <Button variant="text" size="small" startIcon={<Close />} onClick={() => onChange("")} disabled={uploading || saving}>Quitar</Button> : null}
      </Stack>
      {uploading ? <LinearProgress variant="determinate" value={uploadProgress} /> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
      <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden onChange={handleFile} />

      <Dialog open={Boolean(sourceUrl)} onClose={cancelCrop} fullWidth maxWidth="sm">
        <DialogTitle>Ajustar {label.toLowerCase()}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">Acomoda la imagen antes de subirla. El recorte se guardará en el almacenamiento de LYPRO.</Typography>
            <Box sx={{ width: "100%", maxHeight: 420, aspectRatio, overflow: "hidden", bgcolor: "#080a0f", borderRadius: 1, display: "grid", placeItems: "center" }}>
              <canvas ref={canvasRef} style={{ display: "block", width: "100%", maxHeight: 420, objectFit: "contain" }} />
            </Box>
            <Stack direction="row" spacing={1} alignItems="center"><Crop fontSize="small" /><Typography variant="body2">Zoom</Typography></Stack>
            <Slider aria-label="Zoom de la imagen" value={zoom} min={1} max={3} step={0.05} onChange={(_, next) => setZoom(next as number)} />
            <Typography variant="body2">Ajuste horizontal</Typography>
            <Slider aria-label="Ajuste horizontal de la imagen" value={horizontal} min={-1} max={1} step={0.05} onChange={(_, next) => setHorizontal(next as number)} />
            <Typography variant="body2">Ajuste vertical</Typography>
            <Slider aria-label="Ajuste vertical de la imagen" value={vertical} min={-1} max={1} step={0.05} onChange={(_, next) => setVertical(next as number)} />
            {error ? <Alert severity="error">{error}</Alert> : null}
            {uploading ? <Stack spacing={0.5}><LinearProgress variant="determinate" value={uploadProgress} /><Typography variant="caption">Subiendo imagen: {Math.round(uploadProgress)}%</Typography></Stack> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={cancelCrop} disabled={saving || uploading}>Cancelar</Button>
          <Button variant="contained" onClick={() => void uploadCroppedImage()} disabled={!imageReady || saving || uploading} startIcon={saving || uploading ? <CircularProgress size={16} color="inherit" /> : <PhotoCamera />}>
            {saving || uploading ? "Subiendo…" : "Recortar y subir"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
